import { LOGO_URL, fmtDate } from "@/lib/api";

export default function SignedDeliveryNote({ invoice, signatureData }) {
  if (!invoice || !signatureData) return null;
  const co = invoice.company_details || {};
  const { signed_by, signed_at, signature_base64, items_confirmed } = signatureData;

  const displayItems = items_confirmed?.length
    ? items_confirmed
    : (invoice.line_items || []).map((it) => ({ ...it, confirmed: true }));

  return (
    <div className="bg-white p-6 sm:p-8">
      {/* Header */}
      <div className="flex justify-between items-start pb-4 border-b-2" style={{ borderColor: "#032352" }}>
        <div className="max-w-xs">
          <img src={LOGO_URL} crossOrigin="anonymous" alt="J&L" className="h-16 object-contain mb-2" />
          <div className="text-xs text-[#5B5C5F] leading-relaxed">
            <p className="font-semibold text-[#032352] text-sm">{co.name || "J&L Industrial Supplies"}</p>
            {co.address && <p className="whitespace-pre-line">{co.address}</p>}
            {co.phone && <p>Tel: {co.phone}</p>}
          </div>
        </div>
        <div className="text-right">
          <h2 className="font-heading text-2xl font-extrabold text-[#032352]">DELIVERY NOTE</h2>
          <p className="font-mono-num text-[#5B5C5F] mt-1">{invoice.invoice_number}</p>
          <span
            style={{
              display: "inline-block",
              backgroundColor: "#10b981",
              color: "#fff",
              fontSize: "10px",
              fontWeight: "700",
              padding: "4px 10px",
              borderRadius: "999px",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              lineHeight: "1.5",
              marginTop: "8px",
            }}
          >
            Signed
          </span>
        </div>
      </div>

      {/* Address + Dates */}
      <div className="grid grid-cols-2 gap-6 py-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">Deliver To</p>
          <p className="font-semibold text-[#032352]">{invoice.customer_name}</p>
          {invoice.customer_address && (
            <p className="text-sm text-[#5B5C5F] whitespace-pre-line">{invoice.customer_address}</p>
          )}
          {invoice.customer_phone && <p className="text-sm text-[#5B5C5F]">{invoice.customer_phone}</p>}
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-8">
            <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Date</span>
            <span className="font-medium text-[#032352]">{fmtDate(invoice.invoice_date)}</span>
          </div>
          {invoice.order_number && (
            <div className="flex justify-between gap-8">
              <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Order No</span>
              <span className="font-medium text-[#032352]">{invoice.order_number}</span>
            </div>
          )}
        </div>
      </div>

      {/* Items */}
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: "#032352" }} className="text-white">
            <th className="p-3 text-center font-medium w-16">Status</th>
            <th className="p-3 text-left font-medium">Description</th>
            <th className="p-3 text-center font-medium w-24">Qty</th>
          </tr>
        </thead>
        <tbody>
          {displayItems.map((it, i) => (
            <tr key={i} className={`border-b border-slate-200 ${!it.confirmed ? "bg-red-50" : ""}`}>
              <td className="p-3 text-center font-bold text-base">
                {it.confirmed ? (
                  <span style={{ color: "#10b981" }}>✓</span>
                ) : (
                  <span style={{ color: "#ef4444" }}>✗</span>
                )}
              </td>
              <td className="p-3 text-[#032352]">
                {it.title}
                {it.variant ? <span className="text-[#5B5C5F]"> — {it.variant}</span> : null}
                {!it.confirmed && (
                  <span className="ml-2 text-xs font-medium" style={{ color: "#ef4444" }}>
                    Not delivered
                  </span>
                )}
              </td>
              <td className="p-3 text-center font-mono-num text-[#032352] font-semibold">
                {it.quantity}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Signature section */}
      <div className="mt-8 pt-6 border-t-2" style={{ borderColor: "#032352" }}>
        <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-5">Delivery Confirmation</p>
        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#5B5C5F]">Received By</p>
              <p className="font-semibold text-[#032352] mt-1 text-lg">{signed_by}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#5B5C5F]">Date Received</p>
              <p className="font-medium text-[#032352] mt-1">{fmtDate(signed_at)}</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">Signature</p>
            {signature_base64 && (
              <img
                src={signature_base64}
                alt="Signature"
                className="border border-slate-200 rounded bg-white"
                crossOrigin="anonymous"
                style={{ maxWidth: "100%", height: 100, objectFit: "contain" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
